/**
 * Badania i BHP (spec 03 §5) — status liczy serwer z realnych dat jedną
 * regułą z listą kadr (fala 8, grupy 26–28): skierowanie w toku to różowe
 * „Do zrobienia" z terminem, a ukończone szkolenie jednorazowe — „Zaliczone".
 */
import { useTranslation } from 'react-i18next'
import { useQuery } from '@tanstack/react-query'
import { Card, EmptyState, ShieldPlus, Spinner, StatusPill } from '@k1p4/ui'
import { formatDate } from '@k1p4/i18n'
import { ONE_TIME_BHP_KINDS, type BhpTrainingKind, type HealthState } from '@k1p4/shared'
import { api } from '../../api/client'
import styles from './portal.module.css'

const TONE: Record<HealthState, 'success' | 'warning' | 'danger' | 'pink' | 'neutral'> = {
  ok: 'success',
  warn: 'warning',
  err: 'danger',
  todo: 'pink',
  na: 'neutral',
  missing: 'warning',
}

export function PortalHealth() {
  const { t } = useTranslation()
  const health = useQuery({ queryKey: ['me', 'health'], queryFn: () => api.meHealth() })

  if (health.isPending) return <Spinner label={t('common.loading')} />
  if (health.isError) {
    return <EmptyState title={t('common.error')} description={t('common.retry')} />
  }

  const { medical, bhp } = health.data
  const empty = medical.length === 0 && bhp.length === 0

  return (
    <>
      <p className={styles.lead}>{t('portal.health.lead')}</p>

      {empty ? (
        <EmptyState
          icon={<ShieldPlus size={28} />}
          title={t('portal.health.empty')}
          description={t('portal.health.emptyBody')}
        />
      ) : (
        <Card pad="flush" className={styles.list}>
          {medical.map((item) => (
            <div key={item.id} className={styles.row}>
              <span className={styles.rowIcon} aria-hidden="true">
                <ShieldPlus size={18} />
              </span>
              <div className={styles.rowBody}>
                <p className={styles.rowTitle}>{t('portal.health.medical')}</p>
                <p className={styles.rowMeta}>
                  {item.validity === 'todo' && item.referralDueOn
                    ? t('portal.health.referralDue', { date: formatDate(item.referralDueOn, 'dot') })
                    : item.validUntil
                      ? t('portal.health.medicalMeta', {
                          date: formatDate(item.validUntil, 'dot'),
                        })
                      : t('portal.health.medicalNoDate')}
                </p>
              </div>
              <StatusPill tone={TONE[item.validity]}>
                {t(`portal.health.validity.${item.validity}`)}
              </StatusPill>
            </div>
          ))}

          {bhp.map((item) => (
            <div key={item.id} className={styles.row}>
              <span className={styles.rowIcon} aria-hidden="true">
                <ShieldPlus size={18} />
              </span>
              <div className={styles.rowBody}>
                <p className={styles.rowTitle}>
                  {t(`portal.health.kinds.${item.kind}`, { defaultValue: t('portal.health.bhp') })}
                </p>
                <p className={styles.rowMeta}>
                  {item.validity === 'todo' && item.referralDueOn
                    ? t('portal.health.referralDue', { date: formatDate(item.referralDueOn, 'dot') })
                    : ''}
                  {item.performedAt
                    ? t('portal.health.bhpMeta', { date: formatDate(item.performedAt, 'dot') })
                    : ''}
                  {item.validUntil
                    ? ` · ${t('portal.health.bhpValid', { date: formatDate(item.validUntil, 'dot') })}`
                    : ''}
                  {item.conductedBy ? ` · ${t('portal.health.bhpBy', { who: item.conductedBy })}` : ''}
                </p>
              </div>
              <StatusPill tone={TONE[item.validity]}>
                {/* Ukończony instruktaż jednorazowy bez terminu jest zaliczony, a nie „aktualny". */}
                {item.validity === 'ok' &&
                item.validUntil === null &&
                ONE_TIME_BHP_KINDS.includes(item.kind as BhpTrainingKind)
                  ? t('portal.health.passed')
                  : t(`portal.health.validity.${item.validity}`)}
              </StatusPill>
            </div>
          ))}
        </Card>
      )}
    </>
  )
}
